import {
  ITextEntity,
  IBookTextLevel
} from '@shared/types';
import { removeEmptyHtmlElements } from './text-cleaning.utils';
import { encryptionService } from '../services/core';
import { 
  pipelineTextAsHtmlForMishna, 
  pipelineTextAsHtmlForTanakh, 
  pipelineCleanTextForMishna,
  pipelineCleanTextForTanakh
} from './text-pipelines.utils';
import { splitTextByDelimiters } from './text-processing.utils';
import { parseLineToWords } from './line-parsing.utils';

const processHtmlLines = (htmlTextLines: string | string[], isMishna: boolean): string[] => {
  if (Array.isArray(htmlTextLines)) {
    return removeEmptyHtmlElements(htmlTextLines);
  }
  return isMishna ? [htmlTextLines] : splitTextByDelimiters(htmlTextLines);
};

const applyPipeline = (lines: string[], pipeline: ReadonlyArray<(text: string) => string>): string[] => {
  return lines.map(line => Array.from(pipeline).reduce((acc, fn) => fn(acc), line));
};

const createTextEntity = ({ index, id, label, htmlTextLines }: {
  index: number,
  id: number,
  label: string,
  htmlTextLines: string | string[],
}, selectedBookName?: string): ITextEntity => {
  const isMishna = selectedBookName === "Mishna";
  const selectedPipelineForHtml = isMishna ? pipelineTextAsHtmlForMishna : pipelineTextAsHtmlForTanakh;
  const selectedPipelineForClean = isMishna ? pipelineCleanTextForMishna : pipelineCleanTextForTanakh;

  const rawLines = processHtmlLines(htmlTextLines, isMishna);
  const processedLines = applyPipeline(rawLines, selectedPipelineForHtml);
  const joinedHTML = processedLines.join(' ');
  const cleanedText = selectedPipelineForClean.reduce((acc, fn) => fn(acc), joinedHTML);

  const originalWordsArray = processedLines.flatMap(line => {
    const lineContent = line.replace(/^<p[^>]*>(.*)<\/p>$/i, '$1');
    return parseLineToWords(lineContent);
  });

  return {
    index,
    id,
    label,
    htmlTextLines: processedLines,
    cleanedText,
    cleanWordsArray: cleanedText.split(' ').filter(word => word.trim() !== ''),
    originalWordsArray,
  };
};

export { createTextEntity };

export const formatResponseToTextEntities = (
  response: /* ISefariaTextResponse | ITopic[] |  */ IBookTextLevel[],
  selectedBookName?: string
): ITextEntity[] => {
  // טקסט פשוט - מחזיר מערך של טקסטים ITopic[] | IBookTextLevel[
  const entities = response.map((item, index) => {
    let text: string | string[] = item.text ?? '';

    // טיפול בטקסט מוצפן - עבור מחרוזת בודדת (תנ"ך)
    if (typeof text === 'string' && text.includes("U2F")) {
      try {
        text = encryptionService.decrypt(text);
      } catch {
        console.error("Decryption failed, using original text");
      }
    }
    
    // טיפול בטקסט מוצפן - עבור מערך של שורות (משנה)
    if (Array.isArray(text)) {
      text = text.map((line: string) => {
        if (typeof line === 'string' && line.includes("U2F")) {
          try {
            return encryptionService.decrypt(line);
          } catch {
            console.error("Decryption failed for line, using original text");
            return line;
          }
        }
        return line;
      });
    }

    // לא עוטף כל מילה בתגית, רק משאיר את הטקסט כמו שהוא (כולל תגיות HTML קיימות)
    const entity = createTextEntity({
      index: 'index' in item ? Number(item.index) : index,
      id: 'id' in item ? Number(item.id) : index,
      label: item.label ?? '',
      htmlTextLines: text
    }, selectedBookName);
        
    return entity;
  });
  
  return entities;
}; 


